import Box from "@mui/material/Box";
import { styled, CSSObject } from "@mui/material/styles";

interface OwnerState {
  variant?: string;
  bgColor?: string;
  color?: string;
  opacity?: number;
  borderRadius?: string;
  shadow?: string;
}

// Define extended theme types
interface ExtendedPalette {
  gradients?: Record<string, { main: string; state: string }>;
  grey?: Record<number, string>;
  white?: { main: string };
  [key: string]: any;
}

interface ExtendedTheme {
  palette: ExtendedPalette;
  functions?: {
    linearGradient?: (color1: string, color2: string) => string;
  };
  borders?: {
    borderRadius?: Record<string, string | number>;
  };
  boxShadows?: Record<string, string>;
}

// Extend MUI Box with ownerState prop
export default styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerState",
})<{ ownerState: OwnerState }>(({ theme, ownerState }): CSSObject => {
  const extendedTheme = theme as unknown as ExtendedTheme;
  const { palette } = extendedTheme;
  const { functions, borders, boxShadows } = extendedTheme;
  const { variant, bgColor, color, opacity, borderRadius, shadow } = ownerState;

  const { gradients, grey, white } = palette;
  const { linearGradient } = functions || {};
  const { borderRadius: radius } = borders || {};
  const shadowList = boxShadows || {};

  // Define grey color map safely
  const greyColors = {
    "grey-100": grey?.[100],
    "grey-200": grey?.[200],
    "grey-300": grey?.[300],
    "grey-400": grey?.[400],
    "grey-500": grey?.[500],
    "grey-600": grey?.[600],
    "grey-700": grey?.[700],
    "grey-800": grey?.[800],
    "grey-900": grey?.[900],
  } as const;

  type GreyKey = keyof typeof greyColors;

  const validGradients = [
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ] as const;

  const validColors = [
    "transparent",
    "white",
    "black",
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
    "text",
    "grey-100",
    "grey-200",
    "grey-300",
    "grey-400",
    "grey-500",
    "grey-600",
    "grey-700",
    "grey-800",
    "grey-900",
  ] as const;

  const validBorderRadius = [
    "xs",
    "sm",
    "md",
    "lg",
    "xl",
    "xxl",
    "section",
  ] as const;

  const validBoxShadows = [
    "xs",
    "sm",
    "md",
    "lg",
    "xl",
    "xxl",
    "inset",
  ] as const;

  // Helper function to resolve color values
  const resolveColorValue = (colorKey?: string): string | undefined => {
    if (!colorKey || !validColors.includes(colorKey as any)) {
      return colorKey;
    }

    const paletteColor = palette[colorKey];
    return paletteColor?.main || greyColors[colorKey as GreyKey];
  };

  // Helper function to resolve background value
  const resolveBackgroundValue = (): string | undefined => {
    if (variant === "gradient") {
      return validGradients.includes(bgColor as any)//NOSONAR
        ? linearGradient?.(
            gradients?.[bgColor!]?.main ?? "",
            gradients?.[bgColor!]?.state ?? ""
          )
        : white?.main;
    }
    return resolveColorValue(bgColor);
  };

  // Helper function to resolve mapped values
  const resolveMappedValue = <T,>(
    value: string | undefined,
    validValues: readonly string[],
    valueMap: Record<string, T>
  ): T | string | undefined => {
    if (!value || !validValues.includes(value)) {
      return value;
    }
    return valueMap[value];
  };

  return {
    opacity,
    background: resolveBackgroundValue(),
    color: resolveColorValue(color),
    borderRadius: resolveMappedValue(
      borderRadius,
      validBorderRadius,
      radius || {}
    ),
    boxShadow: resolveMappedValue(shadow, validBoxShadows, shadowList || {}),
  };
});
